import { useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  Bot,
  CalendarClock,
  CheckCircle2,
  MessageCircle,
  Search,
  UserRound,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const demoConversations = [
  {
    id: "conv-1",
    name: "Carlos Ramirez",
    phone: "667 123 4567",
    intent: "property_interest",
    status: "hot",
    property: "Local en Stanza Cantabria",
    lastMessage: "Me interesa verlo esta semana, seria para farmacia.",
    followUp: "24 horas",
    acceptedFutureOptions: true,
    summary:
      "Interesado en local para farmacia. Tiene intencion de visita y fecha cercana. Falta confirmar presupuesto final.",
    messages: [
      ["user", "Buenos dias, sigue disponible el local de Cantabria?"],
      ["agent", "Hola, con gusto te ayudo. Para orientarte mejor, dime que giro te gustaria poner y para cuando necesitas ocupar el espacio."],
      ["user", "Seria para farmacia y me interesa verlo esta semana."],
      ["agent", "Perfecto. Para perfilar mejor tu solicitud, me confirmas tu presupuesto mensual aproximado?"],
    ],
    fields: {
      giro: "Farmacia",
      presupuesto: "Pendiente",
      fecha: "Esta semana",
      zona: "Stanza Cantabria",
      visita: "Si",
    },
  },
  {
    id: "conv-2",
    name: "Mariana Lopez",
    phone: "999 555 1122",
    intent: "property_search",
    status: "medium",
    property: "Sin propiedad especifica",
    lastMessage: "Busco terreno en Merida norte, aun no tengo fecha.",
    followUp: "48 horas",
    acceptedFutureOptions: true,
    summary:
      "Busca terreno en Merida norte. Hay interes real, pero falta fecha y presupuesto claro.",
    messages: [
      ["user", "Hola, busco terreno en Merida norte."],
      ["agent", "Claro, te ayudo a buscar opciones. Que giro quieres poner y cual es tu presupuesto aproximado?"],
      ["user", "Seria para tienda o farmacia, pero aun no tengo fecha."],
      ["agent", "Entiendo. Si aparece una propiedad con esas condiciones, te la podemos enviar por aqui?"],
    ],
    fields: {
      giro: "Tienda o farmacia",
      presupuesto: "Pendiente",
      fecha: "Sin fecha",
      zona: "Merida norte",
      visita: "No todavia",
    },
  },
  {
    id: "conv-3",
    name: "Dane Loria",
    phone: "222 391 5038",
    intent: "owner_publish",
    status: "owner_prospect",
    property: "Terreno en renta",
    lastMessage: "Tengo un terreno en renta y quiero publicarlo.",
    followUp: "24 horas si faltan fotos",
    acceptedFutureOptions: false,
    summary:
      "Duena interesada en publicar terreno. Falta completar datos, fotos y disponibilidad.",
    messages: [
      ["user", "Tengo un terreno en renta y quiero publicarlo."],
      ["agent", "Perfecto, te puedo ayudar a publicar tu propiedad en Tulocalmx. Para empezar, dime que tipo de propiedad es, en que ciudad esta y si es renta o venta."],
      ["user", "Es terreno en Merida, renta."],
      ["agent", "Gracias. Me compartes colonia, precio, medidas y fotos para avanzar?"],
    ],
    fields: {
      giro: "Publicacion de terreno",
      presupuesto: "No aplica",
      fecha: "Pendiente",
      zona: "Merida",
      visita: "No aplica",
    },
  },
];

const statusLabels = {
  hot: "Lead caliente",
  medium: "Lead medio",
  initial: "Interesado inicial",
  owner_prospect: "Dueno publicador",
  not_interested: "No interesado",
};

const statusClass = {
  hot: "border-emerald-200 bg-emerald-50 text-emerald-800",
  medium: "border-amber-200 bg-amber-50 text-amber-800",
  initial: "border-blue-200 bg-blue-50 text-blue-800",
  owner_prospect: "border-orange-200 bg-orange-50 text-orange-800",
  not_interested: "border-slate-200 bg-slate-50 text-slate-600",
};

const AdminAgenteConversaciones = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(demoConversations[0].id);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return demoConversations.filter((conversation) => {
      const matchesStatus = statusFilter === "all" || conversation.status === statusFilter;
      const matchesQuery =
        !normalizedQuery ||
        [conversation.name, conversation.phone, conversation.property, conversation.lastMessage]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      return matchesStatus && matchesQuery;
    });
  }, [query, statusFilter]);

  const selected =
    filtered.find((conversation) => conversation.id === selectedId) ||
    filtered[0] ||
    demoConversations[0];

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8">
      <section className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <button
              type="button"
              onClick={() => navigate("/admin/agente-whatsapp")}
              className="mb-3 inline-flex items-center gap-2 text-sm font-black text-blue-700"
            >
              <ArrowLeft size={16} />
              Volver al agente
            </button>
            <p className="text-xs font-black uppercase tracking-wide text-orange-600">
              Conversaciones del agente
            </p>
            <h1 className="mt-1 text-2xl font-black text-slate-950">
              Bandeja de WhatsApp
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Aqui vas a revisar mensajes, corregir errores, agregar notas y mejorar el agente.
              Esta primera vista usa ejemplos; despues se conecta a Supabase.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500"
            aria-label="Salir"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mb-5 grid gap-3 sm:grid-cols-4">
          {[
            ["Todas", demoConversations.length],
            ["Calientes", demoConversations.filter((item) => item.status === "hot").length],
            ["Medios", demoConversations.filter((item) => item.status === "medium").length],
            ["Duenos", demoConversations.filter((item) => item.status === "owner_prospect").length],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-bold uppercase text-slate-500">{label}</p>
              <p className="mt-1 text-2xl font-black text-slate-950">{value}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)_340px]">
          <aside className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 p-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Buscar por nombre, telefono o propiedad"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-orange-400"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none"
              >
                <option value="all">Todos los estados</option>
                <option value="hot">Lead caliente</option>
                <option value="medium">Lead medio</option>
                <option value="initial">Interesado inicial</option>
                <option value="owner_prospect">Dueno publicador</option>
              </select>
            </div>

            <div className="max-h-[720px] overflow-y-auto p-2">
              {filtered.map((conversation) => (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => setSelectedId(conversation.id)}
                  className={`mb-2 w-full rounded-xl border p-3 text-left transition ${
                    selected.id === conversation.id
                      ? "border-orange-200 bg-orange-50"
                      : "border-slate-100 bg-white hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-black text-slate-950">{conversation.name}</p>
                      <p className="text-xs font-bold text-slate-500">{conversation.phone}</p>
                    </div>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black ${statusClass[conversation.status]}`}>
                      {statusLabels[conversation.status]}
                    </span>
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm leading-5 text-slate-600">
                    {conversation.lastMessage}
                  </p>
                </button>
              ))}
            </div>
          </aside>

          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                  <UserRound size={21} />
                </span>
                <div>
                  <h2 className="text-xl font-black text-slate-950">{selected.name}</h2>
                  <p className="text-sm font-bold text-slate-500">{selected.phone}</p>
                  <p className="mt-1 text-sm text-slate-600">{selected.property}</p>
                </div>
              </div>
              <span className={`rounded-full border px-3 py-1 text-xs font-black ${statusClass[selected.status]}`}>
                {statusLabels[selected.status]}
              </span>
            </div>

            <div className="max-h-[650px] space-y-4 overflow-y-auto bg-slate-50 p-5">
              {selected.messages.map(([role, body], index) => (
                <div
                  key={`${role}-${index}`}
                  className={`flex ${role === "agent" ? "justify-start" : "justify-end"}`}
                >
                  <div
                    className={`max-w-[78%] rounded-2xl px-4 py-3 text-sm leading-6 shadow-sm ${
                      role === "agent"
                        ? "border border-slate-200 bg-white text-slate-700"
                        : "bg-[#0056D2] text-white"
                    }`}
                  >
                    <div className="mb-1 flex items-center gap-2 text-xs font-black opacity-80">
                      {role === "agent" ? <Bot size={14} /> : <MessageCircle size={14} />}
                      {role === "agent" ? "Agente" : "Usuario"}
                    </div>
                    {body}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-100 p-4">
              <p className="rounded-xl bg-amber-50 px-4 py-3 text-xs font-bold leading-5 text-amber-900">
                Cuando conectemos Supabase, aqui veras mensajes reales desde `whatsapp_messages`.
                Tambien podras marcar errores y crear correcciones para mejorar el agente.
              </p>
            </div>
          </section>

          <aside className="space-y-5">
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="font-black text-slate-950">Datos del lead</h2>
              <div className="mt-4 space-y-3">
                {Object.entries(selected.fields).map(([label, value]) => (
                  <div key={label} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                    <p className="text-[10px] font-black uppercase text-slate-400">{label}</p>
                    <p className="mt-1 text-sm font-bold text-slate-800">{value}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="font-black text-slate-950">Resumen</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">{selected.summary}</p>
              <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-3">
                <div className="flex items-center gap-2 text-sm font-black text-blue-900">
                  <CalendarClock size={16} />
                  Seguimiento
                </div>
                <p className="mt-1 text-sm font-bold text-blue-800">{selected.followUp}</p>
              </div>
              {selected.acceptedFutureOptions ? (
                <p className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm font-black text-emerald-800">
                  <CheckCircle2 size={16} />
                  Acepta recibir opciones futuras
                </p>
              ) : (
                <p className="mt-3 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-sm font-black text-slate-600">
                  <AlertCircle size={16} />
                  Sin permiso para opciones futuras
                </p>
              )}
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="font-black text-slate-950">Acciones futuras</h2>
              <div className="mt-3 grid gap-2">
                {[
                  "Editar nivel del lead",
                  "Agregar nota interna",
                  "Corregir respuesta del agente",
                  "Enviar lead al dueno",
                  "Pausar seguimiento",
                ].map((action) => (
                  <button
                    key={action}
                    type="button"
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-bold text-slate-700"
                  >
                    {action}
                  </button>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default AdminAgenteConversaciones;
